import React from 'react';
import { Box, Dialog, DialogContent, useMediaQuery, useTheme } from '@mui/material';
import 'react-advanced-cropper/dist/style.css';
import type { UploadReceiptResponse } from '../../../services/auditService';
import { useEditReceiptDialogLogic } from '../../../hooks/logic/useEditReceiptDialogLogic';
import EditReceiptControlsPanel from './EditReceiptControlsPanel';
import EditReceiptDialogActions from './EditReceiptDialogActions';
import EditReceiptDialogHeader from './EditReceiptDialogHeader';
import EditReceiptPreviewPanel from './EditReceiptPreviewPanel';

interface EditReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (response: UploadReceiptResponse) => void;
  queueId: string;
  storeId: string;
  reportType?: string;
  formData: any;
}

const EditReceiptDialog: React.FC<EditReceiptDialogProps> = React.memo(({
  open,
  onClose,
  onSuccess,
  queueId,
  storeId,
  reportType,
  formData,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    selectedFile,
    sourceFile,
    previewUrl,
    loading,
    uploading,
    error,
    locations,
    selectedLocationId,
    locationsLoading,
    locationsResolved,
    editMode,
    modeSwitching,
    perspectiveLoading,
    perspectiveKey,
    horizontalTilt,
    verticalTilt,
    rotation,
    freeCropPreviewBlob,
    cropperRef,
    perspectiveCropperRef,
    freeCropRef,
    onCropperChange,
    onRotationChange,
    onHorizontalTiltChange,
    onVerticalTiltChange,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleLocationChange,
    handleFreeCropPreview,
    handleFreeCropEdit,
    handleFreeCropRevert,
    handlePerspectiveState,
    handleSubmit,
    handleClose,
    setError,
  } = useEditReceiptDialogLogic({
    open,
    queueId,
    storeId,
    reportType,
    formData,
    onClose,
    onSuccess,
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: '16px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: { xs: '100dvh', sm: '90dvh' },
          maxHeight: { xs: '100dvh', sm: '90dvh' },
        },
      }}
    >
      <EditReceiptDialogHeader onClose={handleClose} />
      <DialogContent
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          scrollbarGutter: 'stable',
          px: 2.5,
          py: 1.5,
        }}
      >
        <Box
          paddingY={2}
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            minHeight: 0,
            height: '100%',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            gap: 2,
          }}
        >
          <EditReceiptControlsPanel
            error={error}
            onClearError={() => setError(null)}
            locations={locations}
            selectedLocationId={selectedLocationId}
            locationsLoading={locationsLoading}
            locationsResolved={locationsResolved}
            onLocationChange={handleLocationChange}
            selectedFile={selectedFile}
            previewUrl={previewUrl}
            editMode={editMode}
            freeCropPreviewBlob={freeCropPreviewBlob}
            modeSwitching={modeSwitching}
            rotation={rotation}
            horizontalTilt={horizontalTilt}
            verticalTilt={verticalTilt}
            onFileSelect={handleFileSelect}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onFreeCropPreview={handleFreeCropPreview}
            onFreeCropEdit={handleFreeCropEdit}
            onFreeCropRevert={handleFreeCropRevert}
            onRotationChange={onRotationChange}
            onHorizontalTiltChange={onHorizontalTiltChange}
            onVerticalTiltChange={onVerticalTiltChange}
          />
          <EditReceiptPreviewPanel
            previewUrl={previewUrl}
            loading={loading}
            modeSwitching={modeSwitching}
            editMode={editMode}
            perspectiveKey={perspectiveKey}
            rotation={rotation}
            selectedFile={sourceFile || selectedFile}
            perspectiveLoading={perspectiveLoading}
            cropperRef={cropperRef}
            perspectiveCropperRef={perspectiveCropperRef}
            freeCropRef={freeCropRef}
            onCropperChange={onCropperChange}
            onPerspectiveState={handlePerspectiveState}
          />
        </Box>
      </DialogContent>
      <EditReceiptDialogActions
        previewUrl={previewUrl}
        uploading={uploading}
        modeSwitching={modeSwitching}
        perspectiveLoading={perspectiveLoading}
        locationRequired={locations.length > 1}
        selectedLocationId={selectedLocationId}
        locationsLoading={locationsLoading}
        locationsResolved={locationsResolved}
        onCancel={handleClose}
        onSubmit={handleSubmit}
      />
    </Dialog>
  );
});

export default EditReceiptDialog;
